/**
 * Plot constants shared by controls (§10.7) and rendering (§10.8): the series
 * color palette, server point targets, and the color-scale gradients used by
 * the scatter color-by preview now and the renderer later. Mirrors the blob.
 */

import type { PlotState } from '../../state/plot-state.ts';

/**
 * Non-focused series opacity while hovering one. uPlot's default (0.3) leaves
 * the rest too legible to pick the hovered line out of a crowded overlay.
 */
export const FOCUS_DIM_ALPHA = 0.15;

/** Series palette, cycled by series position (blob `PLOT_COLORS`). */
export const PLOT_COLORS = [
  '#6384ff', '#51cf66', '#ffd43b', '#ff6b6b', '#c49cff',
  '#3bc9db', '#ff922b', '#20c997', '#e599f7', '#94d82d',
  '#fd7e14', '#4c6ef5', '#f06595', '#37b24d', '#7950f2',
];

// High enough that a year at the 3-minute cadence (~175k rows) is drawn as is;
// LTTB only thins ranges longer than that.
export const PLOT_TARGET_POINTS = 200_000;
export const SCATTER_TARGET_POINTS = 100_000;
/** Map track point cap - enough to trace a voyage, light enough for Leaflet
 *  canvas markers + per-point hover tooltips. */
export const MAP_TARGET_POINTS = 4000;

/** First palette entry - the fallback when an index lands outside the array. */
const FALLBACK_COLOR = '#6384ff';

const VIRIDIS_STOPS: Array<[number, string]> = [
  [0, '#440154'], [0.25, '#3b528b'], [0.5, '#21918c'], [0.75, '#5ec962'], [1, '#fde725'],
];

/** Color-scale stops `[offset, hex]`, keyed by name (blob `COLOR_SCALES`). */
export const COLOR_SCALES: Record<string, Array<[number, string]>> = {
  Viridis:  VIRIDIS_STOPS,
  Plasma:   [[0, '#0d0887'], [0.25, '#7e03a8'], [0.5, '#cc4778'], [0.75, '#f89540'], [1, '#f0f921']],
  Inferno:  [[0, '#000004'], [0.25, '#57106e'], [0.5, '#bc3754'], [0.75, '#f98e09'], [1, '#fcffa4']],
  Turbo:    [[0, '#30123b'], [0.17, '#4662d7'], [0.33, '#36aaf9'], [0.5, '#1ae4b6'], [0.67, '#72fe5e'], [0.83, '#faba39'], [1, '#7a0403']],
  CoolWarm: [[0, '#3b4cc0'], [0.25, '#7b9ff9'], [0.5, '#f7f7f7'], [0.75, '#f4a582'], [1, '#b40426']],
  YlOrRd:   [[0, '#ffffb2'], [0.25, '#fecc5c'], [0.5, '#fd8d3c'], [0.75, '#f03b20'], [1, '#bd0026']],
};

/**
 * Ramps that run out to both sides of a neutral middle.
 *
 * A correlation matrix needs one: on a sequential ramp -0.9 and +0.9 are simply
 * two colours, and the sign - usually the first thing being read - has to be
 * looked up in the legend rather than seen.
 */
export const DIVERGING_SCALES: ReadonlySet<string> = new Set(['CoolWarm']);

/** The diverging ramp a signed chart falls back to. */
export const DIVERGING_DEFAULT = 'CoolWarm';

/**
 * For a *numeric* color-by column, discrete coloring is offered only when the
 * plotted column has fewer than this many distinct values - beyond that a
 * continuous gradient reads better and the palette would start repeating colors.
 * Categorical (text) columns have no gradient fallback, so they always offer
 * discrete up to `CATEGORICAL_MAX_UNIQUE`.
 */
export const DISCRETE_MAX_UNIQUE = 10;

/**
 * Max distinct values a categorical (non-numeric) color-by column may have to be
 * colored discretely. Mirrors the backend cap and the `PLOT_COLORS` palette
 * length; beyond it the backend drops the coloring with a warning.
 */
export const CATEGORICAL_MAX_UNIQUE = 15;

/** The reserved `color_scale` value that selects categorical coloring. A bare
 *  `'Discrete'` uses the qualitative palette; `'Discrete:<Colormap>'` samples
 *  the swatches from a named gradient (e.g. `'Discrete:Turbo'`). */
export const DISCRETE_SCALE = 'Discrete';

/** True when `color_scale` selects discrete (categorical) swatches rather than a
 *  continuous gradient. */
export function isDiscreteScale(name: string): boolean {
  return name === DISCRETE_SCALE || name.startsWith(DISCRETE_SCALE + ':');
}

/** The named colormap a `'Discrete:<Colormap>'` scale samples from, or null for
 *  the plain qualitative `'Discrete'` palette. */
export function discreteScaleColormap(name: string): string | null {
  return name.startsWith(DISCRETE_SCALE + ':')
    ? name.slice(DISCRETE_SCALE.length + 1)
    : null;
}

/**
 * Count distinct values, stopping once `cap` is exceeded. Returns a count that
 * may read one past `cap` - callers only care whether it stayed at/under.
 * Early-exit keeps this cheap on large continuous columns. Handles numeric
 * (skips non-finite) and categorical (string) values alike.
 */
export function countUniqueUpTo(
  vals: Array<number | string | null>,
  cap: number,
): number {
  const seen = new Set<number | string>();
  for (let i = 0; i < vals.length; i++) {
    const v = vals[i];
    if (v == null) continue;
    if (typeof v === 'number' && !Number.isFinite(v)) continue;
    seen.add(v);
    if (seen.size > cap) return seen.size;
  }
  return seen.size;
}

/**
 * Distinct non-null color-by values (≤ cap), sorted numerically when all are
 * numbers, else lexically. Returns null when there are more than `cap` distinct
 * values (the caller falls back to a gradient or drops coloring).
 */
export function distinctColorValues(
  vals: Array<number | string | null>,
  cap: number,
): Array<number | string> | null {
  const seen = new Set<number | string>();
  for (let i = 0; i < vals.length; i++) {
    const v = vals[i];
    if (v == null) continue;
    if (typeof v === 'number' && !Number.isFinite(v)) continue;
    seen.add(v);
    if (seen.size > cap) return null;
  }
  if (seen.size === 0) return null;
  const arr = Array.from(seen);
  const allNum = arr.every((v) => typeof v === 'number');
  arr.sort(
    allNum
      ? (a, b) => (a as number) - (b as number)
      : (a, b) => String(a).localeCompare(String(b)),
  );
  return arr;
}

/** Format a color-by value for a discrete legend swatch label. Numbers trim to
 *  4 sig-figs; long category strings are truncated with an ellipsis. */
export function formatColorValue(v: number | string): string {
  if (typeof v === 'string') return v.length > 24 ? v.slice(0, 23) + '…' : v;
  return Number.isInteger(v) ? String(v) : String(Number(v.toPrecision(4)));
}

/** Scale names + their human labels, for the color-scale `<select>`. */
export const COLOR_SCALE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'Viridis', label: 'Viridis' },
  { value: 'Plasma', label: 'Plasma' },
  { value: 'Inferno', label: 'Inferno' },
  { value: 'Turbo', label: 'Turbo' },
  { value: 'CoolWarm', label: 'Cool–Warm' },
  { value: 'YlOrRd', label: 'Yellow–Orange–Red' },
];

/** Discrete color-scale options for the `<select>`: the qualitative palette,
 *  plus one entry per gradient colormap (its swatches sampled evenly from the
 *  map). Shown grouped under "Discrete" alongside the continuous gradients. */
export const DISCRETE_SCALE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: DISCRETE_SCALE, label: 'Categorical' },
  ...COLOR_SCALE_OPTIONS.map((o) => ({
    value: `${DISCRETE_SCALE}:${o.value}`,
    label: o.label,
  })),
];

/** A CSS `linear-gradient(...)` string for the color-scale preview swatch. */
export function colorScaleGradientCss(name: string): string {
  const stops = COLOR_SCALES[name] ?? VIRIDIS_STOPS;
  const parts = stops.map(([off, hex]) => `${hex} ${Math.round(off * 100)}%`);
  return `linear-gradient(to right, ${parts.join(', ')})`;
}

/** Color for a series, honoring a custom override, by its global y position. */
export function seriesColor(state: PlotState, col: string): string {
  const custom = state.custom_colors[col];
  if (custom) return custom;
  const all = [...state.y_left, ...state.y_right];
  const i = all.indexOf(col);
  return PLOT_COLORS[(i >= 0 ? i : 0) % PLOT_COLORS.length] ?? FALLBACK_COLOR;
}

/** Color for a series at an explicit list position, honoring a custom override. */
export function seriesColorAt(state: PlotState, col: string, idx: number): string {
  const custom = state.custom_colors[col];
  if (custom) return custom;
  return PLOT_COLORS[idx % PLOT_COLORS.length] ?? FALLBACK_COLOR;
}

/**
 * Fit color: an explicit override on the fit entry wins, else the base series
 * color so the dashed fit reads as "same series, smoothed" (blob `fitColorFor`).
 */
export function fitColorFor(state: PlotState, series: string, idx: number): string {
  const fit = state.fits.find((f) => f.series === series);
  if (fit?.color) return fit.color;
  return seriesColorAt(state, series, idx);
}

/* ── Multi-ship colour allocation ───────────────────────── */

/**
 * Hue stays with whichever dimension the user is comparing; the other dimension
 * rides a lightness ramp. No dash patterns - they stop being tellable apart
 * somewhere around four, and a ship can easily carry six signals.
 *
 * The ramp spans a band that survives both themes: too light washes out on the
 * light theme, too dark disappears on the dark one. A single step returns the
 * base colour unchanged, so one ship plots exactly as it did before.
 */
const SHADE_LIGHT = 0.74;
const SHADE_DARK = 0.42;

export function hexToHsl(hex: string): [number, number, number] {
  const [r, g, b] = parseHexTriplet(hex).map((v) => v / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, sat, l];
}

export function hslToHex(h: number, sat: number, l: number): string {
  const hue2rgb = (p: number, q: number, t: number): number => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  let r: number;
  let g: number;
  let b: number;
  if (sat === 0) {
    r = l;
    g = l;
    b = l;
  } else {
    const q = l < 0.5 ? l * (1 + sat) : l + sat - l * sat;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const hex = (v: number): string =>
    Math.round(Math.max(0, Math.min(255, v * 255)))
      .toString(16)
      .padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

function parseHexTriplet(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean.split('').map((c) => c + c).join('')
      : clean.padEnd(6, '0').slice(0, 6);
  return [
    parseInt(full.slice(0, 2), 16) || 0,
    parseInt(full.slice(2, 4), 16) || 0,
    parseInt(full.slice(4, 6), 16) || 0,
  ];
}

/** `base` lightened or darkened to step `idx` of `count` along the shade ramp. */
export function shadeOf(base: string, idx: number, count: number): string {
  if (count <= 1) return base;
  const [h, sat] = hexToHsl(base);
  const t = idx / (count - 1);
  return hslToHex(h, sat, SHADE_LIGHT + (SHADE_DARK - SHADE_LIGHT) * t);
}

/** `base` at `alpha`, for the focus mode's faded background ships. */
export function fadeOf(base: string, alpha: number): string {
  const [r, g, b] = parseHexTriplet(base);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Opacity the unfocused ships draw at in `focus` mode. High enough that a
 * background ship is still readable - and that its legend swatch is still
 * identifiable - while the focused ship clearly leads.
 */
export const FOCUS_FADE_ALPHA = 0.6;

/** Distinct values in first-seen order - the ship and column axes of a plot. */
function orderedUnique(values: Array<string | undefined>): string[] {
  const out: string[] = [];
  for (const v of values) if (v && !out.includes(v)) out.push(v);
  return out;
}

/**
 * Build the colour function for one draw.
 *
 * Falls back to the existing single-ship palette whenever the response carries
 * no ship attribution, so nothing about a one-ship plot changes. A custom
 * colour set on a signal becomes that signal's family in the hue-by-signal
 * modes rather than being ignored.
 */
export function buildSeriesColorer(
  state: PlotState,
  series: Array<{ name: string; ship?: string; column?: string }>,
  focusShip: string | null = null,
): (ser: { name: string; ship?: string; column?: string }, idx: number) => string {
  const ships = orderedUnique(series.map((s) => s.ship));
  const columns = orderedUnique(series.map((s) => s.column));
  if (ships.length <= 1 || !columns.length) {
    return (ser, idx) => seriesColorAt(state, ser.column ?? ser.name, idx);
  }

  const mode = state.style.color_mode;
  const focus = focusShip && ships.includes(focusShip) ? focusShip : ships[0]!;

  return (ser, idx) => {
    const ship = ser.ship;
    const column = ser.column ?? ser.name;
    const shipIdx = ship ? ships.indexOf(ship) : 0;
    const colIdx = columns.indexOf(column);
    const custom = state.custom_colors[column];

    switch (mode) {
      case 'hue-ship': {
        const base = PLOT_COLORS[shipIdx % PLOT_COLORS.length] ?? FALLBACK_COLOR;
        return shadeOf(base, Math.max(0, colIdx), columns.length);
      }
      case 'focus': {
        const base =
          custom ?? PLOT_COLORS[Math.max(0, colIdx) % PLOT_COLORS.length] ?? FALLBACK_COLOR;
        return ship === focus ? base : fadeOf(base, FOCUS_FADE_ALPHA);
      }
      case 'unique': {
        const flat = shipIdx * columns.length + Math.max(0, colIdx);
        return PLOT_COLORS[flat % PLOT_COLORS.length] ?? FALLBACK_COLOR;
      }
      case 'hue-signal':
      default: {
        const base =
          custom ?? PLOT_COLORS[Math.max(0, colIdx) % PLOT_COLORS.length] ?? FALLBACK_COLOR;
        return shadeOf(base, shipIdx, ships.length);
      }
    }
  };
}

